import { useCallback, useState } from "react";

import {
  Expand as ExpandIcon,
  Refresh2 as RefreshIcon,
  Shuffle as ShuffleIcon,
  Screen as FullScreenIcon,
  Minimize as MinimizeIcon,
  Image as ImageIcon,
  QuestionMark as QuestionIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { LinkExternal01 } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/General";
import {
  GenArtIFrame,
  generateParams,
} from "@hl/shared-features/lib/features/gen-art/GenArtIFrame";
import { useGenParams } from "@hl/shared-features/lib/features/gen-art/gen-art-utils";
import {
  PreviewStatus,
  usePreviewImage,
} from "@hl/shared-features/lib/features/gen-art/preview";
import {
  ArtworkInteractionType,
  trackArtworkInteraction,
} from "@hl/shared-features/lib/features/gen-art/tracking";
import useGenArtAspectRatio from "@hl/shared-features/lib/hooks/useAspectRatio";
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Loader,
  Modal,
  Tooltip,
  createStyles,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useFullscreen, useMediaQuery } from "@mantine/hooks";

import useMintState from "~hooks/useMintState";

import {
  CapturePreviewTriggerType,
  GenerativeTokenParams,
} from "../../apollo/graphql.generated";

import { GetSalePageCollectionQuery } from "./queries.graphql.generated";

type StyleProps = {
  expanded: boolean;
  responsive: boolean;
};

const useStyles = createStyles(
  (theme, { expanded, responsive }: StyleProps) => {
    const heightPadding = expanded ? 30 : 360;
    return {
      containerResponsive: {
        width: "calc(100% - 120px)",
        height: "75vh",
        minHeight: 200,
        maxHeight: 1200,

        [theme.fn.smallerThan(600)]: {
          width: "calc(100% - 32px)",
          height: "66.67vh",
        },

        ...(expanded && {
          width: "100%",
          height: "100%",

          [theme.fn.smallerThan(600)]: {
            width: "100%",
            height: "100vh",
          },
        }),
      },
      embedContainer: {
        width: "100%",
      },
      container: {
        width: "100%",
        maxWidth: "100%",
        boxShadow: theme.shadows.sm,
        [theme.fn.largerThan("md")]: {
          width: "auto",
          height: `calc(100vh - ${heightPadding}px)`,
          minHeight: 720,
          ["@media (orientation: portrait)"]: {
            width: "100%",
            height: "auto",
            maxHeight: `calc(100vh - ${heightPadding}px)`,
          },
        },
      },
      absolutePosition: {
        position: "absolute",
        top: 0,
      },
      previewImage: {
        objectFit: "scale-down",
      },
      expanded: {
        height: responsive ? "100vh" : "calc(100vh - 30px)",
        overflow: "hidden",
      },
      closeBtn: {
        position: "absolute",
        top: 10,
        right: 10,
      },
      fullscreenButton: {
        [theme.fn.smallerThan("sm")]: {
          display: "none",
        },
      },
      toolbar: {
        boxShadow: theme.shadows.sm,
        borderRadius: 10,
        backgroundColor: theme.colors.primaryBackground[0],
        ...(expanded && {
          position: "absolute",
          bottom: 20,
        }),
        button: {
          height: 36,
          width: 36,
        },
        ...(expanded &&
          responsive && {
            display: "none",
          }),
      },
      modal: {
        ...(responsive && {
          padding: `0px !important`,
        }),
      },
      modalBody: {
        maxHeight: "100%",
        ...(responsive && {
          padding: 0,
          margin: 0,
        }),
      },
    };
  }
);

type Props = {
  collection: GetSalePageCollectionQuery["getPublicCollection"];
  isEmbed?: boolean;
};

export const GenArtHeader = ({ collection, isEmbed = false }: Props) => {
  const { genSeriesIframeElementRef } = useMintState();
  const [opened, { open, close: closeModal }] = useDisclosure(false);
  const responsive = !!collection.generativeDetails?.isResponsive;
  const { classes } = useStyles({ expanded: opened, responsive });
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);
  const isMintControls = collection.flagVariations.enableMintGenSeriesControls;
  const isMintControlsDesktop = isMintControls && !isMobile;

  const details = collection.generativeDetails;
  const defaultParams = collection.generativeDetails?.initialIterationParams;
  const { params, refreshIframe, randomizeAll } = useGenParams({
    allowedHashes: details?.allowedHashes,
    size: collection.size || undefined,
    fixEditionSize: true,
    fixedChainId: collection.chainId,
    fixedContractAddress: collection.address,
    isCurated: !!collection.generativeDetails?.curateOutput,
  });
  const { ref: fullscreenRef, toggle: toggleFullscreen } = useFullscreen();
  const [isImagePreview, setImagePreview] = useState(false);
  const [useDefaultParams, setUseDefaultParams] = useState(!!defaultParams);
  const iconSize = { width: 16, height: 16 };

  const showImage = useCallback(() => {
    setImagePreview(true);
    open();
  }, [open]);
  const close = useCallback(() => {
    closeModal();
    setImagePreview(false);
  }, [closeModal]);

  const [howItWorksOpened, { open: openHowItWorks, close: closeHowItWorks }] =
    useDisclosure(false);

  const mediaUrl = collection.baseUri ?? details?.generativeCodeUri ?? "";
  const url = generateParams({ ...params, mediaUrl });

  const aspectRatio = useGenArtAspectRatio(
    collection.generativeDetails?.captureSettings.viewPort
  );
  const trackUsage = useCallback(
    (action: ArtworkInteractionType) => {
      trackArtworkInteraction({
        collectionId: collection.id,
        interactionType: action,
      });
    },
    [collection]
  );

  const content = (
    <Flex
      direction="column"
      align="center"
      justify="center"
      w={"100%"}
      h={opened ? "100vh" : "auto"}
    >
      <Box
        className={
          isEmbed && !opened
            ? classes.embedContainer
            : responsive
            ? classes.containerResponsive
            : classes.container
        }
        sx={{ aspectRatio, position: "relative" }}
      >
        <GenArtIFrame
          enableRestrictiveFlags={
            collection.flagVariations.enableIframeRestrictiveFlags
          }
          allowIframeSameOrigin={
            collection.flagVariations.allowIframeSameOrigin
          }
          mediaUrl={mediaUrl}
          {...params}
          defaultParams={
            useDefaultParams && defaultParams ? defaultParams : undefined
          }
          extraClass={classes.absolutePosition}
          bordered={false}
          aspectRatio={aspectRatio}
          iframeRef={fullscreenRef}
          iframeElementRef={genSeriesIframeElementRef}
        />
      </Box>
      {(!isMintControls || isMintControlsDesktop) && (
        <Box
          p={4}
          mt={{ base: 16, md: isEmbed ? 16 : 32 }}
          className={classes.toolbar}
        >
          <Group spacing={4}>
            {!isMintControls && (
              <Tooltip label="Reload">
                <ActionIcon
                  onClick={() => {
                    refreshIframe();
                    trackUsage(ArtworkInteractionType.Reload);
                  }}
                >
                  <RefreshIcon {...iconSize} />
                </ActionIcon>
              </Tooltip>
            )}

            {!isMintControls && (
              <Tooltip label="Randomize">
                <ActionIcon
                  onClick={() => {
                    setUseDefaultParams(false);
                    randomizeAll();
                    trackUsage(ArtworkInteractionType.Randomize);
                  }}
                >
                  <ShuffleIcon {...iconSize} />
                </ActionIcon>
              </Tooltip>
            )}
            {!isMintControls && !opened && (
              <Tooltip label="Expand">
                <ActionIcon
                  onClick={() => {
                    open();
                    trackUsage(ArtworkInteractionType.Expand);
                  }}
                >
                  <ExpandIcon {...iconSize} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Fullscreen" className={classes.fullscreenButton}>
              <ActionIcon
                onClick={() => {
                  toggleFullscreen();
                  trackUsage(ArtworkInteractionType.Fullscreen);
                }}
              >
                <FullScreenIcon {...iconSize} />
              </ActionIcon>
            </Tooltip>
            {!isMintControls && (
              <Tooltip label="Preview image">
                <ActionIcon
                  onClick={() => {
                    showImage();
                    trackUsage(ArtworkInteractionType.PreviewImage);
                  }}
                >
                  <ImageIcon {...iconSize} />
                </ActionIcon>
              </Tooltip>
            )}
            {!isMintControls && (
              <Tooltip label="Open in a new window">
                <ActionIcon
                  onClick={() => {
                    window.open(url, "_blank", "noreferrer");
                    trackUsage(ArtworkInteractionType.OpenInNewWindow);
                  }}
                >
                  <LinkExternal01 {...iconSize} />
                </ActionIcon>
              </Tooltip>
            )}
            {!isMintControls && (
              <Tooltip label="How it works">
                <ActionIcon
                  onClick={() => {
                    openHowItWorks();
                    trackUsage(ArtworkInteractionType.HowItWorks);
                  }}
                >
                  <QuestionIcon {...iconSize} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Box>
      )}
      <HowItWorksModal opened={howItWorksOpened} close={closeHowItWorks} />
    </Flex>
  );
  if (opened) {
    return (
      <Modal
        opened={opened}
        onClose={close}
        fullScreen
        withCloseButton={false}
        centered
        classNames={{
          modal: classes.modal,
          body: classes.modalBody,
        }}
      >
        <div className={classes.expanded}>
          {isImagePreview ? (
            <PreviewImage collection={collection} params={params} />
          ) : (
            content
          )}
        </div>
        <ActionIcon className={classes.closeBtn} onClick={close}>
          <MinimizeIcon />
        </ActionIcon>
      </Modal>
    );
  }

  return content;
};

type PreviewProps = {
  collection: GetSalePageCollectionQuery["getPublicCollection"];
  params: GenerativeTokenParams;
};

const PreviewImage = ({ collection, params }: PreviewProps) => {
  const captureSettings = collection?.generativeDetails?.captureSettings;
  const mediaUrl = collection?.generativeDetails?.generativeCodeUri ?? "";
  const responsive = !!collection.generativeDetails?.isResponsive;
  const { previewStatus, imageUrl } = usePreviewImage({
    mediaUrl,
    params,
    trigger: captureSettings?.trigger ?? CapturePreviewTriggerType.Delay,
    delay: captureSettings?.delay ?? 3,
    width: captureSettings?.viewPort?.width ?? 1000,
    height: captureSettings?.viewPort?.height ?? 1000,
    isGPURendering: collection.generativeDetails?.isGPURendering || undefined,
    selector: captureSettings?.selector || undefined,
    selectorType: captureSettings?.selectorType || undefined,
  });
  const { classes, cx } = useStyles({ expanded: true, responsive });
  if (!captureSettings) {
    return null;
  }
  return (
    <Flex justify="center" align="center" pos="relative" h="100%">
      {previewStatus === PreviewStatus.Processing && <Loader size="md" />}
      {previewStatus === PreviewStatus.Done && (
        <img
          className={cx([classes.container, classes.previewImage])}
          src={imageUrl}
        />
      )}
      {previewStatus === PreviewStatus.Failed && <div>Failed to render</div>}
    </Flex>
  );
};

export const HowItWorksModal = ({
  opened,
  close,
}: {
  opened: boolean;
  close: () => void;
}) => {
  return (
    <Modal opened={opened} onClose={close} title="How it works">
      <Text size="sm">
        When minting a token from this generative collection, data from the
        blockchain will be passed to the artist’s code, resulting in an original
        piece of art. Note that the outputs shown here are possible outputs, and
        the token you receive will look different.
      </Text>
      <Text size="sm" mt={16}>
        Press the “Randomize” button to see more possible outputs from this
        collection.
      </Text>
    </Modal>
  );
};
